import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { projectIdParamsSchema } from '../schemas';
import { utilitySchema } from '../../interfaces/IUtility';

// ============================================================
// Body Schemas
// ============================================================

/**
 * `projectId` is absent by construction: the project is addressed by the URI, so
 * a body copy would be a second source of truth the handler could persist
 * instead of the one the guard validated.
 */
const createUtilityBodySchema = utilitySchema.omit({ _id: true, projectId: true });

export type ICreateUtilityBody = z.infer<typeof createUtilityBodySchema>;

// ============================================================
// Contract
// ============================================================

/**
 * Utilities are project data: `/projects/:projectId/utilities`.
 *
 * `{ root: true }` because the paths are absolute now — there is no `/utilities`
 * prefix left to inherit. Every route in this file therefore spells its own full
 * path; both of them are project-scoped, so there is nothing here to leave behind.
 */
export const utilityContract = defineContract(
	'utilities',
	{
		getUtilitiesOfProject: route({
			method: 'GET',
			path: '/projects/:projectId/utilities',
			params: projectIdParamsSchema,
			response: z.array(utilitySchema),
			operationId: 'getUtilitiesOfProject',
			tags: ['Utilities'],
			summary: 'List the utilities of a project',
		}),
		addUtilityForProject: route({
			method: 'POST',
			path: '/projects/:projectId/utilities',
			params: projectIdParamsSchema,
			body: createUtilityBodySchema,
			response: utilitySchema,
			operationId: 'addUtilityForProject',
			tags: ['Utilities'],
			summary: 'Create a utility in a project',
		}),
	},
	{ root: true }
);
